/**
 * This app has no login screen of its own — a session always starts in the main
 * Zooza app, which redirects here with a token. Every "you are not signed in"
 * exit therefore has to point back at the main app *for the environment we are
 * served from*, otherwise a test or regional user is thrown at production.
 *
 * The two are the same host with the `network` label removed:
 *
 *   network.zooza.app        →  zooza.app
 *   network-test.zooza.app   →  test.zooza.app
 *   asia.network.zooza.app   →  asia.zooza.app
 *   uk.network.zooza.app     →  uk.zooza.app
 *
 * Derived rather than listed, so a new region needs no change here. A label of
 * `network-<env>` collapses to `<env>`; a bare `network` label is dropped.
 *
 * Set VITE_MAIN_APP_URL to override.
 */
const NETWORK_LABEL = 'network';

// api-v1 hands out `http://localhost:5174/...` as the onboarding URL in local
// dev, i.e. the main app's own dev server.
const LOCAL_MAIN_APP = 'http://localhost:5174';

const FALLBACK_MAIN_APP = 'https://zooza.app';

export function getMainAppUrl(): string {
    const configured = import.meta.env.VITE_MAIN_APP_URL;

    if (typeof configured === 'string' && configured.length > 0) {
        return configured.replace(/\/+$/, '');
    }

    const host = window.location.hostname;

    if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
        return LOCAL_MAIN_APP;
    }

    const labels = host.split('.');
    const index = labels.findIndex(
        label => label === NETWORK_LABEL || label.startsWith(`${NETWORK_LABEL}-`)
    );

    // Served from something that is not a network host — nothing to derive from.
    if (index === -1) {
        return FALLBACK_MAIN_APP;
    }

    const env = labels[index].slice(NETWORK_LABEL.length + 1);

    if (env) {
        labels[index] = env;
    } else {
        labels.splice(index, 1);
    }

    return `https://${labels.join('.')}`;
}
